import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '@stores/translationStore';
import { apiClient } from '@services/api';
import PageHeader from '@components/PageHeader';

/**
 * Report something left behind — a post-stay module. It reaches the hotel as a message in the
 * guest's own thread, so the hotel's reply shows up in Messages.
 */
export default function LostPropertyPage() {
  const { hotelSlug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tr = (key: string, fallback: string) => (t(key) !== key ? t(key) : fallback);

  const [item, setItem] = useState('');
  const [description, setDescription] = useState('');
  const [whereLeft, setWhereLeft] = useState('');
  const [contact, setContact] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const go = (path: string) => navigate(hotelSlug ? `/${hotelSlug}/${path}` : `/${path}`);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!item.trim()) {
      setError(tr('lostProperty.itemRequired', 'Please tell us what you left behind.'));
      return;
    }
    setBusy(true);
    try {
      await apiClient.post('/api/v1/guest/post-stay/lost-property', { item, description, whereLeft, contact });
      setSent(true);
    } catch {
      setError(tr('lostProperty.failed', 'Could not send your report. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-content">
      <PageHeader
        title={tr('lostProperty.title', 'Lost property')}
        subtitle={tr('lostProperty.subtitle', 'Left something behind? Tell us')}
        leading={<button className="btn btn-link text-white p-0" onClick={() => go('welcome')} aria-label={tr('common.back', 'Back')}>←</button>}
      />
      <div className="py-3">
        {sent ? (
          <div className="text-center py-4">
            <div style={{ fontSize: '3rem' }}>🧳</div>
            <h3 className="h5">{tr('lostProperty.sentTitle', 'We got your report')}</h3>
            <p className="text-muted">{tr('lostProperty.sentText', "We'll look for it and reply in Messages.")}</p>
            <button className="btn btn-primary w-100" onClick={() => go('messages')}>{tr('lostProperty.openMessages', 'Open messages')}</button>
          </div>
        ) : (
          <form onSubmit={submit}>
            {error && <div className="alert alert-danger">{error}</div>}
            <label className="form-label small">{tr('lostProperty.item', 'What did you leave?')} *</label>
            <input className="form-control" value={item} maxLength={200} onChange={(e) => setItem(e.target.value)} />
            <label className="form-label small mt-2">{tr('lostProperty.description', 'Description (colour, brand…)')}</label>
            <textarea className="form-control" rows={3} maxLength={1500} value={description} onChange={(e) => setDescription(e.target.value)} />
            <label className="form-label small mt-2">{tr('lostProperty.where', 'Where do you think you left it?')}</label>
            <input className="form-control" value={whereLeft} maxLength={300} onChange={(e) => setWhereLeft(e.target.value)} />
            <label className="form-label small mt-2">{tr('lostProperty.contact', 'Best way to reach you (optional)')}</label>
            <input className="form-control" value={contact} maxLength={200} onChange={(e) => setContact(e.target.value)} />
            <button type="submit" className="btn btn-primary w-100 mt-4" disabled={busy}>
              {busy ? tr('common.saving', 'Saving…') : tr('lostProperty.send', 'Send report')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
